// Session identity transport for the Vite client and server adapters.
// Minimal NextRequest type for compatibility
interface NextRequest extends Request {
  cookies?: {
    get: (name: string) => { value: string } | undefined
  }
  nextUrl?: URL
}

export const SESSION_HEADER = 'x-intelligence-session-id'
export const SESSION_COOKIE = 'fbc_session_id'
const SESSION_STORAGE_KEY = SESSION_COOKIE

function generateRawSessionId(): string {
  const cryptoApi = globalThis.crypto
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID()
  }
  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint32Array(4)
    cryptoApi.getRandomValues(bytes)
    return Array.from(bytes).map((value) => value.toString(16).padStart(8, '0')).join('')
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

export function normalizeSessionId(raw?: string | null): string {
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  if (!trimmed) {
    return `sess_${generateRawSessionId()}`
  }
  return trimmed
}

function readCookieFromHeader(cookieHeader: string | null): string | undefined {
  if (!cookieHeader) return undefined
  const parts = cookieHeader.split(';')
  for (const part of parts) {
    const [name, value] = part.split('=').map((segment) => segment.trim())
    if (name === SESSION_COOKIE && typeof value === 'string' && value.length > 0) {
      return decodeURIComponent(value)
    }
  }
  return undefined
}

function readCookieFromRequest(req: NextRequest | Request): string | undefined {
  const maybeNext = req as NextRequest
  if (typeof maybeNext.cookies?.get === 'function') {
    const cookie = maybeNext.cookies.get(SESSION_COOKIE)
    if (cookie?.value) {
      return cookie.value
    }
  }
  return readCookieFromHeader(req.headers.get('cookie'))
}

export function getSessionIdFromRequest(req: NextRequest | Request): string {
  const headerValue = req.headers.get(SESSION_HEADER)
  const cookieValue = readCookieFromRequest(req)
  const candidate = headerValue || cookieValue || ''
  return typeof candidate === 'string' ? candidate.trim() : ''
}

function persistClientSessionId(sessionId: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId)
  } catch {
    // Ignore storage failures (Safari private mode, etc.)
  }
  try {
    const maxAge = 60 * 60 * 24 * 30 // 30 days
    document.cookie = `${SESSION_COOKIE}=${encodeURIComponent(sessionId)}; path=/; max-age=${maxAge}; SameSite=Lax`
  } catch {
    // Ignore cookie failures
  }
}

/** Create and persist a new opaque session identity, ignoring prior browser state. */
export function createFreshClientSessionId(): string {
  const sessionId = normalizeSessionId()
  createdOnThisPage.add(sessionId)
  restoredClientSessionId = null
  persistClientSessionId(sessionId)
  return sessionId
}

function readClientStorage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY)
  } catch {
    return null
  }
}

function readClientCookie(): string | null {
  if (typeof document === 'undefined') return null
  return readCookieFromHeader(typeof document.cookie === 'string' ? document.cookie : null) ?? null
}

/** Set when this page restored an id from the cookie or localStorage. */
let restoredClientSessionId: string | null = null
/** Ids this page wrote. A later read of one of these is not a restored session. */
const createdOnThisPage = new Set<string>()

export function getOrCreateClientSessionId(): string {
  if (typeof window === 'undefined') {
    return normalizeSessionId()
  }

  const fromStorage = readClientStorage()
  const fromCookie = readClientCookie()
  const resolved = fromCookie || fromStorage
  const sessionId = normalizeSessionId(resolved)
  if (!resolved || createdOnThisPage.has(sessionId)) {
    createdOnThisPage.add(sessionId)
    restoredClientSessionId = null
  } else {
    restoredClientSessionId = sessionId
  }
  persistClientSessionId(sessionId)
  return sessionId
}

/** True only for the id this page loaded from cookie or localStorage. */
export function clientSessionIdWasRestored(sessionId: string): boolean {
  const normalized = sessionId.trim()
  return Boolean(normalized) && restoredClientSessionId === normalized
}

const capabilityReady = new Set<string>()
const capabilityBootstrapRequests = new Map<string, Promise<void>>()

function assertBootstrapResponse(payload: unknown, sessionId: string): void {
  if (
    !payload ||
    typeof payload !== 'object' ||
    Array.isArray(payload) ||
    (payload as Record<string, unknown>).sessionId !== sessionId
  ) {
    throw new Error('Session capability bootstrap returned an invalid response')
  }
}

/** Establish the server-issued HttpOnly capability required by canonical session endpoints. */
export async function ensureSessionCapability(sessionId: string): Promise<void> {
  const normalizedSessionId = sessionId.trim()
  if (!normalizedSessionId) {
    throw new Error('Session capability bootstrap requires a sessionId')
  }
  if (capabilityReady.has(normalizedSessionId)) return

  const existingRequest = capabilityBootstrapRequests.get(normalizedSessionId)
  if (existingRequest) return existingRequest

  const request = (async () => {
    const response = await fetch('/api/session?action=bootstrap', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_HEADER]: normalizedSessionId,
      },
      body: JSON.stringify({ sessionId: normalizedSessionId }),
    })
    const payload: unknown = await response.json().catch(() => ({}))
    if (!response.ok) {
      const message =
        payload && typeof payload === 'object' && !Array.isArray(payload) &&
        typeof (payload as Record<string, unknown>).error === 'string'
          ? String((payload as Record<string, unknown>).error)
          : 'Failed to establish session authorization'
      throw new Error(message)
    }
    assertBootstrapResponse(payload, normalizedSessionId)
    capabilityReady.add(normalizedSessionId)
  })().finally(() => {
    capabilityBootstrapRequests.delete(normalizedSessionId)
  })

  capabilityBootstrapRequests.set(normalizedSessionId, request)
  return request
}

/** Drop a hung or rejected bootstrap so the next id is not pinned to it. */
export function forgetSessionCapability(sessionId: string): void {
  const normalizedSessionId = sessionId.trim()
  capabilityReady.delete(normalizedSessionId)
  capabilityBootstrapRequests.delete(normalizedSessionId)
}

/** Replace a dead cookie/localStorage id. Does not touch the open transcript. */
export function replaceDeadClientSession(deadId: string): string {
  forgetSessionCapability(deadId)
  if (restoredClientSessionId === deadId.trim()) restoredClientSessionId = null
  return createFreshClientSessionId()
}

/**
 * A restored id that never answers is not a dead session by itself.
 * This is only the cap for a lookup the browser never sees finish.
 * A completed GET /api/session 404 retires before this fires.
 */
export const DEAD_SESSION_LOOKUP_MS = 4_000

let clientSessionLookup: Promise<void> | null = null

/** The plate waits on this so consent is not posted for an id a 404 is about to retire. */
export function trackClientSessionLookup(work: Promise<unknown>): void {
  const settled = work.then(
    () => undefined,
    () => undefined,
  )
  const previous = clientSessionLookup
  const chained = previous ? previous.then(() => settled) : settled
  clientSessionLookup = chained
  void chained.finally(() => {
    if (clientSessionLookup === chained) clientSessionLookup = null
  })
}

export function waitForClientSessionLookup(): Promise<void> {
  const pending = clientSessionLookup
  if (!pending) return Promise.resolve()
  return new Promise(resolve => {
    const timer = setTimeout(resolve, DEAD_SESSION_LOOKUP_MS)
    void pending.then(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}

/** Mint a short-lived capability scoped to one Live connection. */
export async function issueLiveSessionCapability(sessionId: string): Promise<string> {
  const normalizedSessionId = sessionId.trim()
  await ensureSessionCapability(normalizedSessionId)
  const response = await fetch('/api/session?action=live-token', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      [SESSION_HEADER]: normalizedSessionId,
    },
    body: JSON.stringify({ sessionId: normalizedSessionId }),
  })
  const payload: unknown = await response.json().catch(() => ({}))
  if (
    !response.ok ||
    !payload ||
    typeof payload !== 'object' ||
    Array.isArray(payload) ||
    (payload as Record<string, unknown>).sessionId !== normalizedSessionId ||
    typeof (payload as Record<string, unknown>).sessionCapability !== 'string'
  ) {
    const message =
      payload && typeof payload === 'object' && !Array.isArray(payload) &&
      typeof (payload as Record<string, unknown>).error === 'string'
        ? String((payload as Record<string, unknown>).error)
        : 'Failed to authorize Live session'
    throw new Error(message)
  }
  return String((payload as Record<string, unknown>).sessionCapability)
}

export function withSessionHeaders(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers || {})
  const sessionId = getOrCreateClientSessionId()
  headers.set(SESSION_HEADER, sessionId)
  return {
    ...init,
    headers,
  }
}
